/**
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import isEmpty from 'lodash.isempty';
import {PageTitle} from '@ion/applications/src/pages/admin/AdminContainer';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {normalize} from '@ion/core/src/utils/StringUtil';
import {
  PREFERENCE_NAME,
  SavedColumnItem,
  SavedColumnType
} from './types';
import {getSavedColumnsForPage} from './useSavedColumnView';

export interface useSavedColumnListProps {
  pageName?: PageTitle;
};

export type useSavedColumnListReturnType = {
  savedColumnViews: SavedColumnItem[];
  selectedColumnName: string;
  isNotAllowToCreate?: boolean;
  getSavedColumnList?: () => void;
  updateSavedColumns?: (savedColumnName: string, isNotAllowToSave?: boolean) => void;
  removeSavedColumnViews?: (savedColumnName: string) => void;
}

/**
 * @useSavedColumnList
 *
 * Provides a Hook which is used to get saved column views and selectedColumnName.
 * @updateSavedColumns method invoke when a user select a new saved column in dropdown list and
 * it updates the selectedColumnName.
 * @getSavedColumnList method invoke when a user click on Saved Column dropdown or after
 * created a new saved column view to get updated saved column views list and selectedColumnName.
 * @removeSavedColumnViews method invoke when user choose to delete a saved column view.
 *
 * @param pageName It is current page name.
 * @returns It returns savedColumnViews, selectedColumnName, updateSavedColumns method
 * and getSavedColumnList method.
 *
 * ```typescript
 *
 *     const {savedColumnViews,
 *       selectedColumnName,
 *       updateSavedColumns,
 *       getSavedColumnList,
 *       removeSavedColumnViews} = useSavedColumnList({pageName});
 *
 *     ...
 *
 *    <TertiaryButton ...
 *      disabled={savedColumnViews?.length < 1}
 *    ... >
 *      {translate('common:columnChooser.savedColumns')}
 *    </TertiaryButton>
 *
 *    <SavedColumnList ...
 *      savedColumnViews={savedColumnViews}
 *      selectedColumnName={selectedColumnName}
 *      updateSavedColumns={updateSavedColumns}
 *      removeSavedColumnViews={removeSavedColumnViews}
 *      ... />
 *
 *    <SaveColumnEditor ...
 *      getSavedColumnList={getSavedColumnList}
 *      ... />
 *
 * ```
 * @see SavedColumnContainer
 */
export const useSavedColumnList = ({
  pageName
}: useSavedColumnListProps): useSavedColumnListReturnType => {
  const [savedColumnViews, setSavedColumnViews]     = useState<SavedColumnItem[]>([]);
  const [selectedColumnName, setSelectedColumnName] = useState('');
  const [isNotAllowToCreate, setIsNotAllowToCreate] = useState(false);

  /**
   * Get the savedColumns data and update the savedColumnViews and
   * SelectedColumnName state accordingly.
   */
  const getSavedColumnList = useCallback(function() {
    const savedColumnsForPage = getSavedColumnsForPage(pageName);
    const {views, selectedViewName, isNotAllowToCreate: notAllowed} = savedColumnsForPage || {};
    setSavedColumnViews(views || []);
    setSelectedColumnName(selectedViewName);
    setIsNotAllowToCreate(notAllowed);
  }, [pageName]);

  useEffect(() => {
    getSavedColumnList();
  }, [pageName]);

  /**
   * Invoke when the user update the selectedViewName with a new savedColumnName.
   */
  const updateSavedColumns = useCallback(function(savedColumnName: string, isNotAllowToSave = false) {
    const name                = normalize(savedColumnName || '');
    const savedColumns        = UserPreferences.get(PREFERENCE_NAME) ?? [];
    const savedColumnsForPage = getSavedColumnsForPage(pageName);
    if (!isEmpty(savedColumnsForPage)) {
      savedColumnsForPage.selectedViewName = name;
      savedColumnsForPage.isNotAllowToCreate = isNotAllowToSave;
      const restSavedColumns = savedColumns?.filter(
        (page: SavedColumnType) => page.pageName !== pageName);
      UserPreferences.set(PREFERENCE_NAME, [...restSavedColumns, savedColumnsForPage]);
      setSelectedColumnName(name);
      setIsNotAllowToCreate(isNotAllowToSave);
    }
  }, [pageName]);

  /**
   * invoke when user choose to delete a saved column view.
   */
  const removeSavedColumnViews = useCallback(function(savedColumnName: string) {
    const name                = normalize(savedColumnName || '');
    const savedColumns        = UserPreferences.get(PREFERENCE_NAME) ?? [];
    const savedColumnsForPage = getSavedColumnsForPage(pageName);
    if (!isEmpty(savedColumnsForPage)) {
      if (savedColumnsForPage.selectedViewName === name) {
        savedColumnsForPage.selectedViewName = '';
        savedColumnsForPage.isNotAllowToCreate = true;
        setIsNotAllowToCreate(true);
      }
      const updatedColumns = savedColumnsForPage.views.filter(({viewName}) => viewName !== name);
      savedColumnsForPage.views = updatedColumns;
      const restSavedColumns = savedColumns?.filter(
        (page: SavedColumnType) => page.pageName !== pageName);
      UserPreferences.set(PREFERENCE_NAME, [...restSavedColumns, savedColumnsForPage]);
      getSavedColumnList();
    }
  }, [pageName]);

  //save preferences when component / hook unmounts ...
  useEffect(() => {
    return () => {
      if (!UserPreferences.hasChangeDetection()) {
        UserPreferences.save();
      }
    };
  }, []);

  return {
    savedColumnViews,
    selectedColumnName,
    isNotAllowToCreate,
    updateSavedColumns,
    getSavedColumnList,
    removeSavedColumnViews
  };
};

export default useSavedColumnList;
